import { useEffect } from "react"

import { routeChangeEmitter } from "@/lib/embedded/route-change-emitter"
import { isInternalLink } from "@/lib/is-internal-link"
import { mtLocation } from "@/lib/mt-location"
import { hasShareUriScheme } from "@/lib/route-utils"
import { useRouter } from "@/lib/router"
import {
  handleParamChange,
  handleRouteChange,
} from "@/lib/storage/embedded-thunks"
import { useAppDispatch } from "@/lib/storage/hooks"
import {
  setUISettings,
  UiSettingsSliceState,
} from "@/lib/storage/ui-settings-slice"
import { webStorage } from "@/lib/web-storage"

export function EmbeddedRouteHandler() {
  const dispatch = useAppDispatch()
  const router = useRouter()

  useEffect(() => {
    // load UI settings from webStorage
    const uiSettings = webStorage.tryGetJSON<UiSettingsSliceState>(
      "mindtrip.uiSettings"
    )
    if (uiSettings) {
      dispatch(setUISettings(uiSettings))
    }

    const clickListener = (event: MouseEvent) => {
      if (
        event.target &&
        event.target instanceof HTMLAnchorElement &&
        event.target.tagName === "A" &&
        event.target.hasAttribute("href") &&
        // doesn't open in new tab or window
        event.target.getAttribute("target") === "" &&
        // doesn't download file
        !event.target.hasAttribute("download") &&
        // isn't a sms or tel link
        !hasShareUriScheme(event.target.getAttribute("href")) &&
        isInternalLink(event.target.getAttribute("href"))
      ) {
        event.preventDefault()
        event.stopPropagation()

        void dispatch(
          handleRouteChange({
            url: event.target.getAttribute("href") ?? "",
            router: router,
          })
        )
      }
    }

    // Intercept link clicks
    window.document.addEventListener("click", clickListener, true)

    const routeListener = (url: string) => {
      void dispatch(handleRouteChange({ url, router }))
    }
    routeChangeEmitter.addListener(routeListener)

    const hashListener = () => {
      void dispatch(handleParamChange({ hash: mtLocation.hash, router }))
    }

    window.addEventListener("hashchange", hashListener)

    return () => {
      window.document.removeEventListener("click", clickListener, true)
      routeChangeEmitter.removeListener(routeListener)
      window.removeEventListener("hashchange", hashListener)
    }
  }, [router, dispatch])

  return null
}
